import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CrmAddTestConversationInboundMessageBodyParams } from 'src/api/generated/models/CrmAddTestConversationInboundMessageBodyParams';
import type { CrmAddTestConversationInboundMessageResponse } from 'src/api/generated/models/CrmAddTestConversationInboundMessageResponse';
import type { CrmCreateTestConversationBodyParams } from 'src/api/generated/models/CrmCreateTestConversationBodyParams';
import type { CrmCreateTestConversationResponse } from 'src/api/generated/models/CrmCreateTestConversationResponse';
import { CrmTestConversationsService } from 'src/api/generated/services/CrmTestConversationsService';

const noopCreateTestConversationSuccess = (
  _data: CrmCreateTestConversationResponse
): void => undefined;

const noopAddInboundMessageSuccess = (
  _data: CrmAddTestConversationInboundMessageResponse
): void => undefined;

const createTestConversationRequest = async (
  payload: CrmCreateTestConversationBodyParams
): Promise<CrmCreateTestConversationResponse> => {
  return CrmTestConversationsService.createTestConversation({
    fromPhoneNumber: payload.fromPhoneNumber,
    initialMessage: payload.initialMessage,
    idempotencyKey: payload.idempotencyKey,
    contactDisplayName: payload.contactDisplayName,
    assignedUserId: payload.assignedUserId,
    propertyId: payload.propertyId,
  });
};

const addTestConversationInboundMessageRequest = async ({
  conversationId,
  payload,
}: {
  conversationId: number;
  payload: CrmAddTestConversationInboundMessageBodyParams;
}): Promise<CrmAddTestConversationInboundMessageResponse> => {
  return CrmTestConversationsService.addInboundMessage(conversationId, {
    content: payload.content,
  });
};

/**
 * Mutation hook for creating a super-admin freeform test conversation.
 */
export const useCreateTestConversation = (
  onSuccess: (
    data: CrmCreateTestConversationResponse
  ) => void = noopCreateTestConversationSuccess
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    CrmCreateTestConversationResponse,
    Error,
    CrmCreateTestConversationBodyParams
  >({
    mutationFn: createTestConversationRequest,
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['crm/funnel-conversations'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['crm/documents/funnel-conversations'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['crm/timeline-items/search'],
        }),
      ]);

      onSuccess(data);
    },
  });

  return {
    createTestConversation: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Mutation hook for injecting a synthetic inbound message into an existing test
 * conversation.
 */
export const useAddTestConversationInboundMessage = (
  onSuccess: (
    data: CrmAddTestConversationInboundMessageResponse
  ) => void = noopAddInboundMessageSuccess
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    CrmAddTestConversationInboundMessageResponse,
    Error,
    {
      conversationId: number;
      payload: CrmAddTestConversationInboundMessageBodyParams;
    }
  >({
    mutationFn: addTestConversationInboundMessageRequest,
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['crm/funnel-conversations', variables.conversationId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['crm/funnel-conversations'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['crm/timeline-items/search'],
        }),
      ]);

      onSuccess(data);
    },
  });

  return {
    addInboundMessage: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
